import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router-dom'
import { apiFetch } from '../../lib/api'
import { getToken } from '../../lib/auth'
import { useAuthSession } from '../../lib/authSession'
import { formatOfficerDate } from '../../lib/officerFormat'
import SourceDocumentList from '../documents/SourceDocumentList'
import type { ViewScope, ContextWork } from '../navigation/context'
import { useRead } from '../navigation/useRead'
import { retryWholeGroup } from '../ops/api'
import { ErrorState } from '../state'
import RecoveryBookReviews from './RecoveryBookReviews'
import SourceQuestionReview from './SourceQuestionReview'
import {
  offersWholeRetry,
  parseRecoveryItem,
  parseRecoveryOutcome,
  parseRecoveryPage,
  recoveryHref,
  recoveryQueueDestination,
  recoveryOutcomeText,
  recoveryQuery,
  recoveryStatus,
  additionalRecoveryItems,
  recoveryOwnsSelection,
  type RecoveryItem,
  type RecoveryOutcome,
  type RecoveryProposalGroup,
} from './recovery'
import './recovery.css'

interface Props {
  scope: ViewScope
  work: ContextWork | null
  refresh: number
  onChanged: () => void
}
export default function ContextRecovery(props: Props) {
  const session = useAuthSession()
  return <RecoveryPanel key={JSON.stringify([session, props.scope])} {...props} />
}
function RecoveryPanel({ scope, work, refresh, onChanged }: Props) {
  const [params] = useSearchParams()
  const focused = params.get('recovery') === '1'
  const taskParam = params.get('task')
  const selectedId = recoveryOwnsSelection(work, focused, taskParam) ? taskParam : null
  const [cursor, setCursor] = useState<string | null>(null)
  const [previous, setPrevious] = useState<(string | null)[]>([])
  const [reload, setReload] = useState(0)
  const read = useRead<unknown>(recoveryQuery(scope, { cursor }), refresh + reload)
  const refreshList = () => {
    setCursor(null)
    setPrevious([])
    setReload((value) => value + 1)
  }
  const section = useRef<HTMLElement>(null)
  const parsed = useMemo(() => {
    if (read.loading) return {}
    if (read.error) return { error: read.error }
    try {
      return { page: parseRecoveryPage(read.value, scope, cursor) }
    } catch (error) {
      return { error }
    }
  }, [read.value, read.error, read.loading, scope, cursor])
  useEffect(() => {
    if (focused) section.current?.scrollIntoView({ block: 'start' })
  }, [focused])
  let selectionError: unknown = null
  if (selectedId) {
    try {
      recoveryQuery(scope, { taskId: selectedId })
    } catch (error) {
      selectionError = error
    }
  }
  const page = parsed.page
  const rows = additionalRecoveryItems(page?.items ?? [], work, selectedId)
  if (
    page &&
    !read.loading &&
    !parsed.error &&
    !rows.length &&
    !page.next_cursor &&
    !previous.length &&
    !focused &&
    !selectedId
  )
    return null
  if (!read.loading && !parsed.error && page?.total === 0 && !focused && !selectedId) return null
  return (
    <section
      id="additional-work"
      ref={section}
      className="context-recovery"
      aria-labelledby="additional-work-heading"
      data-testid="context-recovery"
    >
      <header className="context-recovery__heading">
        <h2 id="additional-work-heading">Additional open work</h2>
        <button type="button" className="vbtn" onClick={refreshList}>
          Refresh
        </button>
      </header>
      {read.loading ? <p role="status">Reading additional work…</p> : null}
      {parsed.error ? (
        <ErrorState compact what="additional work" error={parsed.error} onRetry={refreshList} />
      ) : null}
      {page && !read.loading && !parsed.error ? (
        <>
          <ul className="context-recovery__list">
            {rows.map((row) => (
              <li key={row.task_id} data-task-id={row.task_id}>
                <Link
                  to={recoveryQueueDestination(row)?.href ?? recoveryHref(scope, row.task_id)}
                  aria-current={selectedId === row.task_id ? 'true' : undefined}
                >
                  {row.label}
                  {row.subject?.label ? ` · ${row.subject.label}` : ''}
                </Link>
                <span>
                  {recoveryStatus(row.status)} · {formatOfficerDate(row.created_at)}
                </span>
                {row.documents.length ? (
                  <SourceDocumentList
                    documents={[...row.documents]}
                    context={{
                      fundId: scope.fund_id,
                      returnTo:
                        recoveryQueueDestination(row)?.href ?? recoveryHref(scope, row.task_id),
                    }}
                  />
                ) : null}
              </li>
            ))}
          </ul>
          {!rows.length ? (
            <p className="context-recovery__empty">
              {page.next_cursor || previous.length
                ? 'No additional work on this page.'
                : 'No additional open work in this context.'}
            </p>
          ) : null}
          {previous.length || page.next_cursor ? (
            <nav className="context-recovery__pager" aria-label="Additional work pages">
              <button
                type="button"
                className="vbtn"
                disabled={!previous.length}
                onClick={() => {
                  setCursor(previous[previous.length - 1])
                  setPrevious((values) => values.slice(0, -1))
                }}
              >
                Previous
              </button>
              <button
                type="button"
                className="vbtn"
                disabled={!page.next_cursor}
                onClick={() => {
                  setPrevious((values) => [...values, cursor])
                  setCursor(page.next_cursor)
                }}
              >
                Next
              </button>
            </nav>
          ) : null}
        </>
      ) : null}
      {selectionError ? <ErrorState compact what="selected work" error={selectionError} /> : null}
      {selectedId && !selectionError ? (
        <SelectedRecovery
          key={selectedId}
          taskId={selectedId}
          scope={scope}
          refresh={refresh + reload}
          onChanged={onChanged}
        />
      ) : null}
    </section>
  )
}
function SelectedRecovery({
  taskId,
  scope,
  refresh,
  onChanged,
}: {
  taskId: string
  scope: ViewScope
  refresh: number
  onChanged: () => void
}) {
  const [params] = useSearchParams()
  const [reload, setReload] = useState(0)
  const [showBook, setShowBook] = useState(false)
  const [bookGroups, setBookGroups] = useState<readonly RecoveryProposalGroup[]>([])
  const [bookPending, setBookPending] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<unknown>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [outcome, setOutcome] = useState<RecoveryOutcome | null>(null)
  const mounted = useRef(true),
    inFlight = useRef(false)
  const read = useRead<unknown>(recoveryQuery(scope, { taskId }), refresh + reload)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const parsed = useMemo(() => {
    if (read.loading) return {}
    if (read.error) return { error: read.error }
    try {
      return { item: parseRecoveryItem(read.value, scope, taskId) }
    } catch (error) {
      return { error }
    }
  }, [read.value, read.error, read.loading, scope, taskId])
  async function retry(item: RecoveryItem) {
    if (inFlight.current || busy || !offersWholeRetry(item)) return
    const account = getToken()
    if (!account) return
    inFlight.current = true
    setBusy(true)
    setFailure(null)
    setNotice(null)
    setOutcome(null)
    try {
      // The read is not an action grant. Refresh the exact context before
      // invoking the existing writer, which re-admits source/actor/task itself.
      const fresh = parseRecoveryItem(
        await apiFetch<unknown>(recoveryQuery(scope, { taskId })),
        scope,
        taskId
      )
      if (!mounted.current || getToken() !== account) return
      if (fresh.read_revision !== item.read_revision || !offersWholeRetry(fresh)) {
        setNotice('This work changed. Review its current state before retrying.')
        setReload((n) => n + 1)
        return
      }
      const result = parseRecoveryOutcome(await retryWholeGroup(taskId), taskId)
      if (!mounted.current || getToken() !== account) return
      setOutcome(result)
      setReload((n) => n + 1)
      onChanged()
    } catch (error) {
      if (mounted.current && getToken() === account) setFailure(error)
    } finally {
      inFlight.current = false
      if (mounted.current) setBusy(false)
    }
  }
  const item = parsed.item
  const queueDoor = item ? recoveryQueueDestination(item) : null
  if (
    queueDoor?.href &&
    !read.loading &&
    !parsed.error &&
    !busy &&
    !bookPending &&
    !showBook &&
    (params.get('queue') !== queueDoor.kind || params.get('recovery') === '1')
  )
    return <Navigate to={queueDoor.href} replace />
  return (
    <article className="context-recovery__detail" data-testid="recovery-detail">
      {read.loading ? <p role="status">Reading this item…</p> : null}
      {parsed.error ? (
        <ErrorState
          compact
          what="selected work"
          error={parsed.error}
          onRetry={() => setReload((n) => n + 1)}
        />
      ) : null}
      {outcome ? (
        <p
          role={outcome.outcome === 'dead_lettered_again' ? 'alert' : 'status'}
          data-testid="recovery-outcome"
        >
          {recoveryOutcomeText(outcome)}
        </p>
      ) : null}
      {notice ? <p role="status">{notice}</p> : null}
      {failure ? (
        <ErrorState
          compact
          what="retry"
          error={failure}
          onRetry={() => {
            setFailure(null)
            setReload((n) => n + 1)
          }}
        />
      ) : null}
      {item && !read.loading && !parsed.error ? (
        <>
          <h3>
            {item.label}
            {item.subject?.label ? ` · ${item.subject.label}` : ''}
          </h3>
          <p>
            {recoveryStatus(item.status)} · {formatOfficerDate(item.created_at)}
          </p>
          <SourceDocumentList
            documents={[...item.documents]}
            context={{ fundId: scope.fund_id, returnTo: recoveryHref(scope, taskId) }}
          />
          {item.source_question_kind != null ? (
            <SourceQuestionReview
              key={JSON.stringify([item.task_id, scope])}
              item={item}
              scope={scope}
              refresh={refresh + reload}
              onChanged={onChanged}
            />
          ) : null}
          {item.has_other_context_proposals ? (
            <p>
              This task also has proposed work outside this view.{' '}
              <Link to={recoveryHref({ kind: 'all' }, taskId)}>View whole task →</Link>
            </p>
          ) : null}
          {item.proposal_selection_scope === 'view' && offersWholeRetry(item) ? (
            <p>
              This Book review is limited to this view. Retry uses the task’s original approved
              selection.
            </p>
          ) : null}
          <div className="context-recovery__actions">
            {offersWholeRetry(item) ? (
              <button
                type="button"
                className="vbtn pri"
                disabled={busy}
                onClick={() => void retry(item)}
              >
                {busy ? 'Retrying…' : 'Retry'}
              </button>
            ) : null}
            {item.proposal_groups.length || showBook ? (
              <button
                type="button"
                className="vbtn"
                disabled={bookPending}
                onClick={() => {
                  if (!showBook) setBookGroups(item.proposal_groups)
                  setShowBook((value) => !value)
                }}
                aria-expanded={showBook}
              >
                {showBook ? 'Hide Book review' : 'Review proposed work'}
              </button>
            ) : null}
            {item.destinations
              .filter((door) => door.kind === 'fund_overview' && door.href)
              .map((door) => (
                <Link key={door.href} to={door.href!}>
                  Fund Overview →
                </Link>
              ))}
          </div>
        </>
      ) : null}
      {showBook && bookGroups.length ? (
        <RecoveryBookReviews
          groups={bookGroups}
          scope={scope}
          onPendingChange={setBookPending}
          onChanged={() => {
            setReload((n) => n + 1)
            onChanged()
          }}
        />
      ) : null}
    </article>
  )
}
